import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '@/utils/helpers';
import type { CartLine } from '../posTypes';

interface PosCartItemProps {
  line: CartLine;
  onQuantityChange: (id: number, change: number) => void;
  onRemove: (id: number) => void;
  onReselectProductItems: (line: CartLine) => void;
}

const PosCartItem: React.FC<PosCartItemProps> = ({
  line,
  onQuantityChange,
  onRemove,
  onReselectProductItems,
}) => {
  const { t } = useTranslation();

  return (
    <article className="grid min-h-[76px] grid-cols-[auto_minmax(0,1fr)_auto] gap-2.5 border-b border-outline-variant/60 py-2.5 last:border-0" role="listitem">
    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-fixed text-primary">
      <span className="material-symbols-outlined text-[20px]">{line.icon}</span>
    </div>

    <div className="min-w-0">
      <h3 className="truncate font-label-md font-semibold text-on-surface">{line.name}</h3>
      <p className="mt-0.5 truncate text-[12px] text-secondary">
        {line.variant} · {formatCurrency(line.price)}
      </p>

      {line.trackingMode === 'QUANTITY' ? (
        <div className="mt-1.5 flex items-center gap-1.5">
          <button
            type="button"
            aria-label={t('admin.sales.cart.decrease', { name: line.name })}
            onClick={() => onQuantityChange(line.id, -1)}
            className="inline-flex h-7 w-7 items-center justify-center rounded-lg border border-outline-variant text-secondary transition-colors hover:border-primary hover:text-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          >
            <span className="material-symbols-outlined text-[16px]">remove</span>
          </button>
          <span className="w-7 text-center font-mono text-body-sm text-on-surface">{line.quantity}</span>
          <button
            type="button"
            aria-label={t('admin.sales.cart.increase', { name: line.name })}
            disabled={line.quantity >= line.stock}
            onClick={() => onQuantityChange(line.id, 1)}
            className="inline-flex h-7 w-7 items-center justify-center rounded-lg border border-outline-variant text-secondary transition-colors hover:border-primary hover:text-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <span className="material-symbols-outlined text-[16px]">add</span>
          </button>
          <span className="ml-1 text-[11px] text-secondary">
            {t('admin.sales.cart.stock', { count: line.stock })}
          </span>
        </div>
      ) : (
        <div className="mt-1">
          <button
            type="button"
            className="font-label-sm text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-primary/20"
            onClick={() => onReselectProductItems(line)}
          >
            {t('admin.sales.cart.selected_devices', { count: line.quantity })}
          </button>
          <p className="mt-0.5 line-clamp-1 break-all text-[11px] text-secondary">
            {line.selectedProductItems
              .map((item) => item.imei || item.serialNumber || `#${item.id}`)
              .join(', ')}
          </p>
        </div>
      )}
    </div>

    <div className="flex shrink-0 flex-col items-end justify-between gap-1">
      <strong className="whitespace-nowrap font-mono text-body-sm text-on-surface">
        {formatCurrency(line.price * line.quantity)}
      </strong>
      <button
        type="button"
        className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-secondary transition-colors hover:bg-error-container/50 hover:text-error focus:outline-none focus:ring-2 focus:ring-error/20"
        onClick={() => onRemove(line.id)}
        aria-label={t('admin.sales.cart.remove', { name: line.name })}
      >
        <span className="material-symbols-outlined text-[18px]">delete</span>
      </button>
    </div>
    </article>
  );
};

export default PosCartItem;
